import { useGetVesselCatalogMutation } from 'app/api/facade';
import styles from './VesselsPage.module.scss';
import { UserI } from 'app/utils/userSlice';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Area, OrderParams, PortCatalogData, TargetCatalogFilter, TargetCatalogSearchRequest } from 'ais-facade-api/api';
import { Table, Pagination } from 'app/components';

import { columns as tableColumns } from './columns';
import { Button, FilterPanel, Input, PageHeader, Typo, Icon } from 'app/components';

import { useDebounce, useLocalStorage, useOnClickOutside } from 'app/hooks';
import { throttle } from 'app/helpers';
import moment from 'moment';

import { getI18n, useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'app/store/store';
import { setVesselsCatalogFilter } from 'app/utils/facadeReducer';
import { FormEquality } from 'app/helpers/getVesselsFilterFormEquality';

interface Props {
  user: UserI | null;
}

const initialBody: TargetCatalogSearchRequest = {
  filter: {
    fleetOnly: false,
    source: [],
    type: [],
    country: [],
    status: [],
    specialType: ['NONE'],
  },
  order: {
    direction: 'DESC',
    by: 'LUT',
  },
  pagination: {
    offset: 0,
    limit: 20,
  },
};

export interface SortingInterface {
  direction: 'ASC' | 'DESC' | 'default';
  by: string;
}

export interface Filters extends Omit<TargetCatalogFilter, 'bbox'> {
  touched?: boolean;
  formEquality?: FormEquality;

  statusAll: boolean;
  typeAll: boolean;
  countryAll: boolean;
  sourceAll: boolean;
  fleetGroupAll: boolean;
  selectedInPort?: PortCatalogData;
  selectedInArea?: Area;
  selectedDestinationPort?: PortCatalogData;
}

export const VesselsPage = ({ user }: Props) => {
  const { t } = useTranslation();
  const language = useMemo(() => getI18n().language, [t]);
  const dispatch = useAppDispatch();

  const [getCatalog, { data: catalog, ...catalogRequest }] = useGetVesselCatalogMutation({
    fixedCacheKey: 'get-vessels-mutation',
  });

  const debouncedGetCatalogHandler = useCallback(throttle(getCatalog, 500), []);

  const filters = useAppSelector((state) => state.facadeSlice.vesselsCatalogFilter);

  const [currentPage, setCurrentPage] = useState(1);
  const currentOffset = useMemo(() => (currentPage - 1) * 20, [currentPage]);

  const [showFilter, setShowFilter] = useState<boolean>(false);

  const [localFilter, setLocalFilter] = useLocalStorage<Filters | null>('filters-vesselsCatalog', null);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  const initialSort: OrderParams = {
    direction: 'DESC',
    by: 'LUT',
  };

  const [sorting, setSorting] = useState<SortingInterface>(initialSort);

  const [term, setTerm] = useLocalStorage<string>('vesselsCatalogTerm', '');
  const debouncedInputValue = useDebounce<string>(term, 500);

  const columns = useMemo(() => tableColumns, []);

  useEffect(() => {
    if (localFilter !== null) {
      dispatch(setVesselsCatalogFilter(localFilter));
    }
  }, [localFilter]);

  const catalogFilter = useMemo(() => {
    const constructionDate =
      filters?.constructionDate?.from !== undefined || filters?.constructionDate?.to !== undefined
        ? {
            from: filters.constructionDate?.from
              ? moment.utc({ year: Number(filters.constructionDate.from) }).format('YYYY-MM-DDTHH:mm:ssZ')
              : undefined,
            to: filters.constructionDate?.to
              ? moment.utc({ year: Number(filters.constructionDate.to) }).format('YYYY-MM-DDTHH:mm:ssZ')
              : undefined,
          }
        : undefined;

    const constructionDateDatetime = filters?.constructionDateDatetime ? filters.constructionDateDatetime : undefined;

    const eta =
      filters?.eta?.from !== undefined || filters?.eta?.to !== undefined
        ? {
            from: filters.eta?.from ? `${filters.eta?.from}T00:00:00Z` : undefined,
            to: filters.eta?.to ? `${filters.eta?.to}T00:00:00Z` : undefined,
          }
        : undefined;

    const etaDatetime = filters?.etaDatetime ? filters.etaDatetime : undefined;

    return {
      fleetOnly: filters?.fleetOnly || false,
      term: debouncedInputValue.length >= 2 ? debouncedInputValue.toLowerCase() : null,
      source: filters?.sourceAll ? [] : filters?.source,
      type: filters?.typeAll ? [] : filters?.type,
      country: filters?.countryAll ? [] : filters?.country,
      status: filters?.statusAll ? [] : filters?.status,
      specialType: ['NONE'],
      sog: filters?.sog,
      cog: filters?.cog,
      heading: filters?.heading,
      draught: filters?.draught,
      eta: eta || {},
      etaDatetime: etaDatetime || undefined,
      constructionDateDatetime: constructionDateDatetime || undefined,
      inPortKey: filters?.inPortKey || [],
      inAreaCode: filters?.inAreaCode || [],
      destinationPortKey: filters?.destinationPortKey || [],
      constructionDate: constructionDate || {},
    };
  }, [filters, debouncedInputValue]);

  useEffect(() => {
    setCurrentPage(1);

    debouncedGetCatalogHandler({
      ...initialBody,
      filter: catalogFilter,
      pagination: {
        offset: 0,
        limit: 20,
      },
      order:
        sorting.direction === 'default'
          ? initialSort
          : {
              direction: sorting.direction,
              by: sorting.by,
            },
    });
  }, [debouncedInputValue, language]);

  useEffect(() => {
    if (user) {
      debouncedGetCatalogHandler({
        ...initialBody,
        filter: catalogFilter,
        pagination: {
          offset: currentOffset,
          limit: 20,
        },
        order:
          sorting.direction === 'default'
            ? initialSort
            : {
                direction: sorting.direction,
                by: sorting.by,
              },
      });
    }
  }, [currentOffset, user]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);

      debouncedGetCatalogHandler({
        ...initialBody,
        filter: catalogFilter,
        order:
          sorting.direction === 'default'
            ? initialSort
            : {
                direction: sorting.direction,
                by: sorting.by,
              },
      });
    }
  }, [sorting]);

  useEffect(() => {
    if (user) {
      setCurrentPage(1);
      setSorting(initialBody.order);

      debouncedGetCatalogHandler({
        ...initialBody,

        filter: catalogFilter,
      });
    }
  }, [filters]);

  useEffect(() => {
    if (user && catalogRequest.isUninitialized) {
      debouncedGetCatalogHandler({
        ...initialBody,
        filter: catalogFilter,
      });
    }
  }, [catalogRequest.isUninitialized]);

  const clickOutsideHandler = () => {
    setShowFilter(false);
  };
  useOnClickOutside(filterPanelRef, clickOutsideHandler);

  return (
    <div className={styles.page}>
      <PageHeader iconName="catalog-vessels" title={t('VesselsPageHeader')} />
      <div className={styles.table_actions}>
        <Typo
          variant="span"
          size="m"
          value={`${t('CatalogVesselsTotal')}${filters?.touched ? ` ${t('AppliedFilters')}:` : ':'} ${
            catalog ? catalog.total : '—'
          }`}
        />
        <Button variant="general-small" onClick={() => setShowFilter(true)} className={styles.icon_button}>
          {filters?.touched ? <Icon name="filter-active" size="14px" /> : <Icon name="filter" size="14px" />}

          <Typo value={t('Filter')} variant="span" />
        </Button>
        <Input
          className={styles.table_search}
          type="search"
          onChange={(e) => {
            setTerm(e.target.value);
            localStorage.setItem('vesselsCatalogTerm', JSON.stringify(debouncedInputValue));
          }}
          placeholder={t('SearchPlaceholder')}
          value={term}
        />
      </div>

      {catalogRequest.isLoading && <span className={styles.loading}>{t('SearchingText')}</span>}

      {!catalogRequest.isLoading && catalog?.results?.length === 0 && <p className={styles.no_data}>{t('NoResultsFound')}</p>}

      {catalog?.results && catalog.results.length > 0 && catalog?.total && (
        <>
          <div className={styles.table_container}>
            <Table
              manualSort={true}
              sorting={sorting}
              setSorting={setSorting}
              columns={columns}
              data={catalog.results}
              total={catalog.total}
            />
          </div>
          <div className={styles.pagination_container}>
            <Pagination
              totalCount={catalog.total}
              currentPage={currentPage}
              pageChangeHandler={setCurrentPage}
              rowsPerPage={20}
            />
          </div>
        </>
      )}
      {showFilter && (
        <FilterPanel
          innerRef={filterPanelRef}
          onClose={() => {
            setShowFilter(false);
          }}
          place="catalog"
          filtersState={[localFilter, setLocalFilter]}
        />
      )}
    </div>
  );
};
